import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Store, Package, Star, MapPin, Mail, Clock, ChevronRight, X, Settings } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import MobileBottomNav from '../components/MobileBottomNav';
import useAuthStore from '../store/authStore';
import axiosInstance from '../api/axios';
import { imgVariant } from '../utils/imgUrl';
import { formatEUR } from '../utils/format';

// Default Homeyoursoul template colors
const DEFAULT_TEMPLATE = {
  colors: {
    primary: '#C4A084',
    secondary: '#8B7355',
    accent: '#D4C4B0',
    background: '#F5EDE4',
    text: '#5D4E3C',
    text_light: '#8B7355',
    card_bg: '#FFFFFF',
    border: '#D4C4B0'
  }
};

const VendorShop = () => {
  const { vendorId } = useParams();
  const { user, isAuthenticated } = useAuthStore();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shopTemplate, setShopTemplate] = useState(null);
  
  // Check if current user is viewing their own shop
  const isOwnShop = isAuthenticated && user?.id === vendorId && user?.role === 'vendor';

  useEffect(() => {
    loadVendorAndProducts();
  }, [vendorId]);

  const loadVendorAndProducts = async () => {
    try {
      setLoading(true);
      // Use the public vendor endpoint that includes products
      const response = await axiosInstance.get(`/vendors/public/${vendorId}`);
      
      setVendor(response.data.vendor);
      setProducts(response.data.products || []);
      
      // Load template if vendor has one
      const vendorProfile = response.data.vendor?.vendor_profile;
      if (vendorProfile?.shop_template?.template_id) {
        try {
          const templateRes = await axiosInstance.get(`/shop-templates/${vendorProfile.shop_template.template_id}`);
          // Apply custom colors if available
          const customColors = vendorProfile.shop_template.custom_colors;
          if (customColors) {
            setShopTemplate({
              ...templateRes.data,
              colors: { ...templateRes.data.colors, ...customColors }
            });
          } else {
            setShopTemplate(templateRes.data);
          }
        } catch (e) {
          console.log('Template not found, using default');
          setShopTemplate(DEFAULT_TEMPLATE);
        }
      } else {
        setShopTemplate(DEFAULT_TEMPLATE);
      }
    } catch (err) {
      console.error('Error loading vendor:', err);
      setError('Verkäufer nicht gefunden');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long'
    });
  };

  // Get colors from template or defaults
  const colors = shopTemplate?.colors || DEFAULT_TEMPLATE.colors;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-2 border-t-transparent mx-auto mb-4" style={{ borderColor: colors.primary }}></div>
          <p style={{ color: colors.text_light }}>Lädt Shop...</p>
        </div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <Store className="h-16 w-16 mx-auto mb-4" style={{ color: colors.text_light }} />
          <h1 className="text-2xl font-bold mb-2" style={{ color: colors.text }}>Shop nicht gefunden</h1>
          <p className="mb-6" style={{ color: colors.text_light }}>Dieser Verkäufer existiert nicht oder ist nicht mehr aktiv.</p>
          <Link to="/products">
            <Button style={{ backgroundColor: colors.primary, color: '#FFFFFF' }}>Zurück zum Sortiment</Button>
          </Link>
        </div>
        <Footer />
        <MobileBottomNav />
      </div>
    );
  }

  // Get vendor customization
  const shopStyle = vendor.vendor_profile?.shop_customization || {};
  const bannerImage = shopStyle.banner_image || null;

  return (
    <div className="min-h-screen pb-20 lg:pb-0" style={{ backgroundColor: colors.background }}>
      <Navbar />
      
      {/* Owner Banner - Shows when vendor views their own shop */}
      {isOwnShop && (
        <div className="text-white px-4 py-2" style={{ backgroundColor: colors.secondary }}>
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <p className="text-sm">
              Sie sehen Ihren Shop so, wie Kunden ihn sehen.
            </p>
            <Link to="/vendor-dashboard">
              <Button size="sm" variant="secondary" className="bg-white hover:bg-gray-100" style={{ color: colors.secondary }}>
                <X className="h-4 w-4 mr-2" />
                Zurück zum Dashboard
              </Button>
            </Link>
          </div>
        </div>
      )}
      
      {/* Shop Header / Banner */}
      <div 
        className="relative"
        style={{ 
          backgroundColor: bannerImage ? 'transparent' : colors.primary,
          backgroundImage: bannerImage ? `url(${bannerImage})` : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/30"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-12 sm:py-16">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Shop Logo/Avatar */}
            <div className="w-24 h-24 sm:w-32 sm:h-32 bg-white shadow-lg flex items-center justify-center overflow-hidden">
              {vendor.vendor_profile?.shop_logo ? (
                <img 
                  src={vendor.vendor_profile.shop_logo} 
                  alt={vendor.vendor_profile.shop_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Store className="h-12 w-12 sm:h-16 sm:w-16" style={{ color: colors.text_light }} />
              )}
            </div>
            
            {/* Shop Info */}
            <div className="text-center sm:text-left text-white">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">
                {vendor.vendor_profile?.shop_name || 'Shop'}
              </h1>
              
              {vendor.vendor_profile?.shop_description && (
                <p className="text-white/90 max-w-2xl mb-4 text-sm sm:text-base">
                  {vendor.vendor_profile.shop_description}
                </p>
              )}
              
              <div className="flex flex-wrap justify-center sm:justify-start gap-4 text-sm text-white/80">
                <span className="flex items-center gap-1">
                  <Package className="h-4 w-4" />
                  {products.length} Produkte
                </span>
                {vendor.vendor_profile?.approved_at && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    Dabei seit {formatDate(vendor.vendor_profile.approved_at)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <nav className="flex items-center gap-2 text-sm" style={{ color: colors.text_light }}>
          <Link to="/" className="hover:underline" style={{ color: colors.text_light }}>Start</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/products" className="hover:underline" style={{ color: colors.text_light }}>Produkte</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="font-medium" style={{ color: colors.text }}>{vendor.vendor_profile?.shop_name}</span>
        </nav>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        {products.length === 0 ? (
          <div className="text-center py-16">
            <Package className="h-16 w-16 mx-auto mb-4" style={{ color: colors.text_light }} />
            <h2 className="text-xl font-semibold mb-2" style={{ color: colors.text }}>Noch keine Produkte</h2>
            <p style={{ color: colors.text_light }}>Dieser Shop hat noch keine Produkte eingestellt.</p>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-semibold mb-6" style={{ color: colors.text }}>
              Alle Produkte von {vendor.vendor_profile?.shop_name}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {products.map((product) => (
                <Link key={product.id} to={`/products/${product.id}`}>
                  <div 
                    className="h-full hover:shadow-lg transition-all hover:scale-[1.02] overflow-hidden"
                    style={{ 
                      backgroundColor: colors.card_bg,
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    <div className="aspect-square overflow-hidden" style={{ backgroundColor: colors.accent }}>
                      {product.images?.[0] ? (
                        <img 
                          src={imgVariant(product.images[0], { w: 400 })} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="h-8 w-8" style={{ color: colors.text_light }} />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h3 className="font-medium text-sm line-clamp-2 mb-1" style={{ color: colors.text }}>{product.name}</h3>
                      <span 
                        className="inline-block px-3 py-1 text-white font-bold"
                        style={{ backgroundColor: colors.primary }}
                      >
                        {formatEUR(product.price)}
                      </span>
                      {product.stock <= 3 && product.stock > 0 && (
                        <p className="text-xs mt-1" style={{ color: '#D97706' }}>
                          Nur noch {product.stock} verfügbar
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default VendorShop;
